import { useState } from "react";

import {
  normalizedRate,
  rateLimitCeilingOf,
  RATE_LIMIT_CEILING_WINDOW_SECONDS,
  RATE_LIMIT_LANES,
  settingsParamsOf,
  tierRateLimitOf,
  type McpQuotaTier,
  type McpSettings,
  type RateLimitLane,
} from "../../api/mcp";
import type { AppConfig } from "../../config";
import { Button, Input } from "../../f7";
import { useResource } from "../../hooks/useResource";
import { useWrite } from "../../hooks/useWrite";
import { Gate } from "../authority/Gate";
import { Badge, Card, Panel } from "../ui";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { effectFromSeconds, WriteResult } from "../write/EffectTiming";

/**
 * One tier's two rate-limit lanes.
 *
 * REPLACES `components/settings/RateLimitEditor`, which wrote
 * `PATCH /admin/ratelimit/{lane}` — a route MCP has retired and now 404s.
 * Rate limits are part of the tier document: they are set with the caps, by
 * `PATCH /admin/quota/{tier}` with a `ratelimit` object, and they belong on
 * the tier's page rather than in global Settings. A lane set globally was
 * always a fiction; it is per tier in the runtime and now in the API too.
 *
 * TWO FIELDS PER LANE, ALWAYS SENT TOGETHER. The pair is what expresses a
 * rate, and the reason the old route existed at all: `PATCH /admin/settings`
 * could set `max_requests` without `window_seconds`, and
 * `max_requests=90, per_seconds=1` is 5400/min — enough to trip the block on
 * Foodics' SHARED egress IP, which affects every merchant rather than the one
 * being edited. The API still refuses half a pair.
 *
 * `per_seconds` is the field name now, not the old route's `window_seconds`.
 *
 * The normalized-rate preview mirrors the server's formula so an operator can
 * see a refusal coming, but it NEVER blocks the write on its own arithmetic —
 * the 422 is the only real enforcement, and a client that guessed differently
 * from the server would block changes the server would have accepted.
 */
export function TierRateLimits({
  config,
  tier,
  quota,
  onDone,
}: {
  config: AppConfig;
  tier: string;
  quota: McpQuotaTier | null;
  onDone: () => void;
}) {
  // The ceilings are ordinary read-only rows on GET /admin/settings; there is
  // no dedicated route for them.
  const settings = useResource<McpSettings>(config, "/mcp/admin/settings");
  const params = settings.state.kind === "ready" ? settingsParamsOf(settings.state.data) : [];

  return (
    <Card
      title="Rate limits"
      note="Foodics HTTP requests per window, per merchant on this tier"
      collapsible
    >
      <div className="flex flex-col gap-4">
        {RATE_LIMIT_LANES.map((lane) => (
          <LaneCard
            key={lane}
            config={config}
            tier={tier}
            lane={lane}
            current={tierRateLimitOf(quota, lane)}
            ceiling={rateLimitCeilingOf(params, lane)}
            effectiveWithin={quota?.effective_within_seconds}
            onDone={onDone}
          />
        ))}

        <p className="text-xs/5 text-slate-500 dark:text-slate-400">
          Caps count tool calls; rate limits count Foodics HTTP requests — one report can be
          several. A lane left alone keeps the server&rsquo;s default.
        </p>
      </div>
    </Card>
  );
}

/**
 * One lane, read and written as a pair.
 *
 * Shows whether the rate in force is the TIER'S OWN or the server's code
 * default — `effective.ratelimit.{lane}.source`. An operator editing a code
 * default is creating a stored lane where none existed, which is a different
 * act from adjusting one that is already there.
 */
function LaneCard({
  config,
  tier,
  lane,
  current,
  ceiling,
  effectiveWithin,
  onDone,
}: {
  config: AppConfig;
  tier: string;
  lane: RateLimitLane;
  current: { max_requests?: number; per_seconds?: number; source?: string } | null;
  ceiling: number | null;
  effectiveWithin: number | undefined;
  onDone: () => void;
}) {
  const write = useWrite<McpQuotaTier>(config);
  const [requests, setRequests] = useState("");
  const [seconds, setSeconds] = useState("");
  const [confirming, setConfirming] = useState(false);
  const busy = write.state.kind === "submitting";

  const nextRequests = Number(requests.trim() || current?.max_requests);
  const nextSeconds = Number(seconds.trim() || current?.per_seconds);
  const typed = requests.trim() !== "" || seconds.trim() !== "";
  const valid =
    Number.isInteger(nextRequests) &&
    nextRequests >= 1 &&
    Number.isInteger(nextSeconds) &&
    nextSeconds >= 1;

  // Mirrors the server's check so a refusal is visible before submitting. NOT
  // a gate: the 422 is the enforcement, and the message it returns explains
  // the shared-IP reason better than anything here could.
  const rate = valid ? normalizedRate(nextRequests, nextSeconds, lane) : null;
  const over = rate !== null && ceiling !== null && rate > ceiling;
  const window = RATE_LIMIT_CEILING_WINDOW_SECONDS[lane];

  function submit(reason: string) {
    void write
      .submit(`/mcp/admin/quota/${encodeURIComponent(tier)}`, {
        method: "PATCH",
        // THE LANE GOES WHOLE. `ratelimit` merges entry by entry, so sending
        // one lane leaves the other alone — but a lane itself is always both
        // numbers, because half a pair cannot express a rate.
        body: JSON.stringify({
          ratelimit: { [lane]: { max_requests: nextRequests, per_seconds: nextSeconds } },
          ...(reason ? { reason } : {}),
        }),
      })
      .then((r) => {
        if (!r) return;
        setRequests("");
        setSeconds("");
        onDone();
      });
    setConfirming(false);
  }

  return (
    <Panel className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {lane === "core" ? "Core lane" : "Reports lane"}
        </p>
        {/* `code default` means no stored lane — saving creates one. */}
        {current?.source && current.source !== "tier" ? (
          <Badge tone="amber">{current.source}</Badge>
        ) : (
          <Badge>tier</Badge>
        )}
      </div>

      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
        Now{" "}
        <span className="tabular-nums">
          {current?.max_requests ?? "—"} / {current?.per_seconds ?? "—"}s
        </span>
        {ceiling !== null && (
          <>
            {" · ceiling "}
            <span className="tabular-nums">
              {ceiling} per {window}s
            </span>
          </>
        )}
      </p>

      <Gate need="quota:write">
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="text-xs text-slate-500 dark:text-slate-400">
            Requests
            <div className="mt-1 w-28">
              <Input
                value={requests}
                onChange={(e) => setRequests(e.target.value)}
                placeholder={String(current?.max_requests ?? "")}
                aria-label={`${lane} lane max requests`}
                disabled={busy}
              />
            </div>
          </label>
          <label className="text-xs text-slate-500 dark:text-slate-400">
            Per seconds
            <div className="mt-1 w-28">
              <Input
                value={seconds}
                onChange={(e) => setSeconds(e.target.value)}
                placeholder={String(current?.per_seconds ?? "")}
                aria-label={`${lane} lane per seconds`}
                disabled={busy}
              />
            </div>
          </label>
          <Button disabled={busy || !typed || !valid} onClick={() => setConfirming(true)}>
            Save
          </Button>
        </div>
      </Gate>

      {rate !== null && (
        <p
          className={
            over
              ? "mt-2 text-xs text-red-600 dark:text-red-400"
              : "mt-2 text-xs text-slate-500 dark:text-slate-400"
          }
        >
          Normalizes to <span className="tabular-nums">{rate}</span> per {window}s.
          {over && " This would be refused."}
        </p>
      )}

      {write.state.kind === "done" && (
        <div className="mt-3">
          <WriteResult title={`${lane} lane updated`} onDismiss={write.reset}>
            <p className="text-sm">{effectFromSeconds(effectiveWithin, "This")}</p>
          </WriteResult>
        </div>
      )}
      {write.state.kind === "invalid" && (
        <div className="mt-3">
          {/* The 422 the preview above anticipates. Its message names the
              ceiling, the normalized rate and WHY the ceiling exists — the
              shared egress IP — which is more than a client-side hint can
              say, so it is surfaced verbatim. */}
          <WriteResult tone="amber" title="Refused" onDismiss={write.reset}>
            <p className="text-sm">{write.state.error.message}</p>
          </WriteResult>
        </div>
      )}
      {write.state.kind === "guardRefused" && (
        <div className="mt-3">
          <WriteResult tone="amber" title="Refused by a guard" onDismiss={write.reset}>
            <p className="text-sm">{write.state.error.message}</p>
          </WriteResult>
        </div>
      )}
      {write.state.kind === "idleReauth" && (
        <div className="mt-3">
          <WriteResult tone="amber" title="Nothing was written" onDismiss={write.reset}>
            <p className="text-sm">
              This session has been idle, so the change was not sent. Confirming your identity
              now — retry once you are back.
            </p>
          </WriteResult>
        </div>
      )}
      {write.state.kind === "signedout" && (
        <div className="mt-3">
          <WriteResult tone="amber" title="Not written" onDismiss={write.reset}>
            <p className="text-sm">Your session ended. Sign in again and retry.</p>
          </WriteResult>
        </div>
      )}
      {(write.state.kind === "denied" ||
        write.state.kind === "notfound" ||
        write.state.kind === "unavailable" ||
        write.state.kind === "error") && (
        <div className="mt-3">
          <WriteResult tone="amber" title="Not written" onDismiss={write.reset}>
            <p className="text-sm">{write.state.error.message}</p>
          </WriteResult>
        </div>
      )}

      <ConfirmDialog
        open={confirming}
        title={`Change the ${lane} lane on ${tier}`}
        confirmLabel="Save"
        requireReason
        reasonHint="e.g. DA-1234, merchant reported throttling"
        busy={busy}
        onConfirm={submit}
        onCancel={() => setConfirming(false)}
        effect={effectFromSeconds(effectiveWithin, "This")}
      >
        <p>
          Every merchant on <span className="ref">{tier}</span> gets{" "}
          <span className="tabular-nums">
            {nextRequests} requests / {nextSeconds}s
          </span>{" "}
          on the {lane} lane.
        </p>
        {over && (
          <p className="text-amber-700 dark:text-amber-400">
            This normalizes to {rate} per {window}s, over the ceiling of {ceiling}. MCP will
            refuse it — the ceiling protects our shared egress IP, so `force` cannot override it.
          </p>
        )}
        {current?.source && current.source !== "tier" && (
          <p>
            This lane has no stored value on {tier} — saving creates one, replacing the
            server&rsquo;s default.
          </p>
        )}
      </ConfirmDialog>
    </Panel>
  );
}
